// Flowtech AI: one chat assistant on its own page (backend: server/flowtechAi/routes.js).
// Answers stream in as they are written; tool steps show as small lines; files it makes show as download cards;
// changes to company data wait for Approve / Reject.
import React, { useEffect, useRef, useState } from 'react';
import NavigationBar from '../NavigationBar';
import './flowtechAI.css';

const MAX_FILES = 3;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const SUGGESTIONS = [
  'Show me the orders delivered this week',
  'What is pending in production right now?',
  'Make a PDF summary of the last 10 delivery challans',
  'Read this PO and tell me what the customer ordered',
];

// --- tiny Markdown: bold, italics, code, headings, lists, tables, links. Text is escaped first. ---------------
const esc = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
function inline(s) {
  return esc(s)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<i>$2</i>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>');
}
function markdown(text) {
  const lines = String(text || '').split('\n');
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const cells = (l) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => inline(c.trim()));
      const head = cells(line);
      i += 2;
      const rows = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) rows.push(cells(lines[i++]));
      out.push(`<div class="ftai-table"><table><thead><tr>${head.map((c) => `<th>${c}</th>`).join('')}</tr></thead><tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`);
      continue;
    }
    if (/^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items = [];
      while (i < lines.length && (ordered ? /^\s*\d+\.\s+/ : /^\s*[-*]\s+/).test(lines[i])) items.push(inline(lines[i++].replace(/^\s*([-*]|\d+\.)\s+/, '')));
      out.push(`<${ordered ? 'ol' : 'ul'}>${items.map((t) => `<li>${t}</li>`).join('')}</${ordered ? 'ol' : 'ul'}>`);
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) out.push(`<h${Math.min(6, h[1].length + 2)}>${inline(h[2])}</h${Math.min(6, h[1].length + 2)}>`);
    else if (line.trim()) out.push(`<p>${inline(line)}</p>`);
    i++;
  }
  return out.join('');
}

const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve({ name: file.name, type: file.type, size: file.size, data: String(r.result) });
  r.onerror = () => reject(new Error(`Could not read ${file.name}`));
  r.readAsDataURL(file);
});

// POST and read the answer line by line (one JSON event per line).
async function streamPost(path, body, onEvent) {
  const res = await fetch(`${window.backendUrl}${path}`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = res.status === 401 || res.status === 403 ? 'Your login has expired. Log in again.' : `Server error (${res.status}).`;
    try { const j = await res.json(); if (j.error) msg = j.error; } catch (_) { /* not JSON */ }
    throw new Error(msg);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (line) { try { onEvent(JSON.parse(line)); } catch (_) { /* ignore a bad line */ } }
    }
  }
  if (buf.trim()) { try { onEvent(JSON.parse(buf)); } catch (_) { /* ignore */ } }
}

export default function FlowtechAI() {
  const [messages, setMessages] = useState([]); // { role, text, steps[], files[], approval, attachments[], error, cost }
  const [input, setInput] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [notice, setNotice] = useState('');
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { endRef.current && endRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages]);

  // Applies one streamed event to the last (assistant) message.
  const patchLast = (fn) => setMessages((ms) => {
    const copy = ms.slice();
    copy[copy.length - 1] = fn({ ...copy[copy.length - 1] });
    return copy;
  });
  const onEvent = (e) => {
    if (e.type === 'conversation') return setConversationId(e.id);
    patchLast((m) => {
      if (e.type === 'text') m.text = (m.text || '') + e.delta;
      else if (e.type === 'tool') m.steps = [...(m.steps || []), e.label];
      else if (e.type === 'file') m.files = [...(m.files || []), e];
      else if (e.type === 'approval') m.approval = { ...e, state: 'waiting' };
      else if (e.type === 'error') m.error = e.error;
      else if (e.type === 'done') { m.cost = (m.cost || 0) + (e.costUSD || 0); m.streaming = false; }
      return m;
    });
  };

  async function send(textArg) {
    const text = (textArg ?? input).trim();
    if (busy || (!text && !files.length)) return;
    const attachments = files;
    setInput('');
    setFiles([]);
    setNotice('');
    setMessages((ms) => [...ms, { role: 'user', text, attachments: attachments.map((f) => f.name) }, { role: 'assistant', text: '', steps: [], files: [], streaming: true }]);
    setBusy(true);
    try {
      await streamPost('/flowtech-ai/chat', {
        conversationId, text, company: localStorage.getItem('company') || '',
        attachments: attachments.map((f) => ({ name: f.name, type: f.type, data: f.data })),
      }, onEvent);
    } catch (err) {
      patchLast((m) => ({ ...m, error: err.message, streaming: false }));
    } finally {
      setBusy(false);
      patchLast((m) => ({ ...m, streaming: false }));
      inputRef.current && inputRef.current.focus();
    }
  }

  async function decide(index, approve) {
    const target = messages[index];
    if (!target || !target.approval || busy) return;
    setMessages((ms) => ms.map((m, i) => (i === index ? { ...m, approval: { ...m.approval, state: approve ? 'approved' : 'rejected' } } : m)));
    setMessages((ms) => [...ms, { role: 'assistant', text: '', steps: [], files: [], streaming: true }]);
    setBusy(true);
    try {
      await streamPost('/flowtech-ai/approve', { conversationId, approvalId: target.approval.id, approve }, onEvent);
    } catch (err) {
      patchLast((m) => ({ ...m, error: err.message, streaming: false }));
    } finally {
      setBusy(false);
      patchLast((m) => ({ ...m, streaming: false }));
    }
  }

  async function addFiles(list) {
    const picked = Array.from(list || []);
    if (files.length + picked.length > MAX_FILES) { setNotice(`Attach at most ${MAX_FILES} files at a time.`); return; }
    const big = picked.find((f) => f.size > MAX_FILE_BYTES);
    if (big) { setNotice(`"${big.name}" is bigger than 5 MB.`); return; }
    try {
      const read = await Promise.all(picked.map(readFile));
      setFiles((fs) => [...fs, ...read]);
      setNotice('');
    } catch (err) { setNotice(err.message); }
  }

  const newChat = () => { if (!busy) { setMessages([]); setConversationId(null); setFiles([]); setNotice(''); } };
  const onPaste = (e) => {
    const imgs = Array.from(e.clipboardData.files || []).filter((f) => f.type.startsWith('image/'));
    if (imgs.length) { e.preventDefault(); addFiles(imgs); }
  };

  return (
    <div className="ftai-page">
      <NavigationBar />
      <div className="ftai-shell" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
        <div className="ftai-top">
          <div className="ftai-brand"><span className="ftai-dot" />Flowtech AI</div>
          <button className="ftai-ghost" onClick={newChat} disabled={busy}>+ New chat</button>
        </div>

        <div className="ftai-scroll">
          {!messages.length && (
            <div className="ftai-empty">
              <h2>How can I help today?</h2>
              <p>Ask about orders, production, delivery, stock or accounts. Attach a PO (PDF, Excel or a photo), or ask for a PDF or Excel report.</p>
              <div className="ftai-suggest">
                {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)}>{s}</button>)}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`ftai-msg ftai-${m.role}`}>
              {m.role === 'user' ? (
                <div className="ftai-bubble">
                  {m.attachments && m.attachments.length > 0 && <div className="ftai-attached">{m.attachments.map((n) => <span key={n}>📎 {n}</span>)}</div>}
                  {m.text}
                </div>
              ) : (
                <div className="ftai-answer">
                  {m.steps && m.steps.length > 0 && <div className="ftai-steps">{m.steps.map((s, j) => <div key={j}>✓ {s}</div>)}</div>}
                  {m.text && <div className="ftai-md" dangerouslySetInnerHTML={{ __html: markdown(m.text) }} />}
                  {m.streaming && !m.text && !m.approval && <div className="ftai-typing"><span /><span /><span /></div>}
                  {m.files && m.files.map((f, j) => (
                    <a key={j} className="ftai-file" href={f.url} target="_blank" rel="noopener noreferrer">
                      <span className="ftai-file-kind">{(f.kind || 'file').toUpperCase()}</span>
                      <span>{f.name}<small>Click to download · link works for 30 minutes</small></span>
                    </a>
                  ))}
                  {m.approval && (
                    <div className={`ftai-approval ftai-${m.approval.state}`}>
                      <div className="ftai-approval-title">Approval needed: {m.approval.title}</div>
                      <pre>{JSON.stringify(m.approval.args, null, 2)}</pre>
                      {m.approval.state === 'waiting' ? (
                        <div className="ftai-approval-buttons">
                          <button className="ftai-approve" disabled={busy} onClick={() => decide(i, true)}>Approve</button>
                          <button className="ftai-reject" disabled={busy} onClick={() => decide(i, false)}>Reject</button>
                        </div>
                      ) : <div className="ftai-approval-state">{m.approval.state === 'approved' ? 'Approved' : 'Rejected'}</div>}
                    </div>
                  )}
                  {m.error && <div className="ftai-error">{m.error}</div>}
                  {!m.streaming && m.cost > 0 && <div className="ftai-cost">≈ ${m.cost.toFixed(4)}</div>}
                </div>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="ftai-composer">
          {(files.length > 0 || notice) && (
            <div className="ftai-chips">
              {files.map((f, i) => <span key={i} className="ftai-chip">📎 {f.name}<button onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}>×</button></span>)}
              {notice && <span className="ftai-notice">{notice}</span>}
            </div>
          )}
          <div className="ftai-inputrow">
            <button className="ftai-attach" title="Attach PDF, Excel or photo" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>📎</button>
            <input ref={fileRef} type="file" multiple hidden accept=".pdf,.xlsx,.xls,.xlsm,.csv,.txt,image/*" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            <textarea
              ref={inputRef} rows={1} value={input} placeholder="Message Flowtech AI…  (Enter to send, Shift+Enter for a new line)"
              onChange={(e) => setInput(e.target.value)} onPaste={onPaste}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <button className="ftai-send" onClick={() => send()} disabled={busy || (!input.trim() && !files.length)}>{busy ? '…' : 'Send'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
